import { Dialog } from "@mui/material";
import { X } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import API from "../../API/newIndex";
import ImageUploader from "../../controllers/ImageUploader";
import { Transition } from "../../controllers/Transition";
import Notification from "../Notification";
import { InputComponent, ButtonComponent, SelectComponent } from '@shopeasy-dashboard-ui/ui-library';
import TextareaComponent from "../FormInput/TextareaComponent";

const AddPortfolioComments = ({
  commentRef,
  isAddComment,
  closeAddComment,
  type,
  editCommentData,
}) => {
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const textareaRef = useRef(null);

  useEffect(() => {
    if (isAddComment && type === "edit") {
      const timer = setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const length = textareaRef.current.value?.length || 0;
          textareaRef.current.setSelectionRange?.(length, length);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAddComment, type, editCommentData]);

  const [commentData, setCommentData] = useState({
    name: "",
    profile_picture: "",
    comment: "",
    portfolioId: "",
  });

  useEffect(() => {
    if (editCommentData) {
      setCommentData({
        name: editCommentData?.name,
        profile_picture: editCommentData?.profile_picture,
        comment: editCommentData?.comment,
        portfolioId: editCommentData?.portfolioId?._id,
      });
    }
  }, [editCommentData]);

  const handleSetImage = (value) => {
    setCommentData({ ...commentData, profile_picture: value });
  };

  const [allPortfolios, setAllPortfolios] = useState([]);
  const [loading, setLoading] = useState(false);

  const GetDefaultPortfolios = async () => {
    try {
      setLoading(true);
      const data = await API({
        url: "/admin/portfolio/getPortfolios",
        method: "get",
        params: { is_draft: false, limit: "all" },
      });
      if (data.status === 200 || data.status === 304) {
        const records = data?.data?.data?.records || [];
        setAllPortfolios(records);
      }
    } catch (error) {
      setSeverity("error");
      setIsNotification(true);
      setNotificationMsg(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAddComment) {
      GetDefaultPortfolios();
    }
  }, [isAddComment]);

  const handleAddPortfolioComment = async (e) => {
    e.preventDefault();
    if (type === "edit") {
      try {
        const payload = commentData;
        await API({
          url: `/admin/portfolio/UpdatePortfolioComment/${editCommentData?._id}`,
          method: "put",
          data: payload,
        });
        closeAddComment("call api");
        setIsNotification(true);
        setNotificationMsg("portfolio comment updated successfully");
        setSeverity("success");
        setCommentData({
          name: "",
          profile_picture: "",
          comment: "",
          portfolioId: "",
        });
      } catch (error) {
        setIsNotification(true);
        setNotificationMsg(error);
        setSeverity("error");
      }
    } else {
      try {
        const payload = commentData;
        await API({
          url: `/admin/portfolio/AddAdminComment`,
          method: "post",
          data: payload,
        });
        closeAddComment("call api");
        setCommentData({
          name: "",
          profile_picture: "",
          comment: "",
          portfolioId: "",
        });
      } catch (error) {
        setIsNotification(true);
        setNotificationMsg(error);
        setSeverity("error");
      }
    }
  };

  const isDisabled =
    commentData?.name === "" ||
    commentData?.portfolioId === "" ||
    commentData?.comment === "";

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <Dialog
        open={isAddComment}
        TransitionComponent={Transition}
        onClose={() => {
          closeAddComment();
          setCommentData({
            name: "",
            profile_picture: "",
            comment: "",
            portfolioId: "",
          });
        }}
        aria-describedby="alert-dialog-slide-description"
      >
        <div className="h-auto secondary-bg-color overflow-y-auto p-5 sm:p-6">
          <div className="flex items-end justify-between w-full">
            <h6 className="text-xl md:text-2xl font-semibold text-primary">
              {`${type === "edit" ? "Update" : "Add"} portfolio comment`}
            </h6>
            <ButtonComponent
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                closeAddComment();
                setCommentData({
                  name: "",
                  profile_picture: "",
                  comment: "",
                  portfolioId: "",
                });
              }}
              className="close-btn !p-1"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </ButtonComponent>
          </div>
          <hr className="my-4 main-border-color" />
          <div className="flex flex-col sm:flex-row w-full sm:w-[550px] justify-evenly mx-auto">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              method="POST"
              className="space-y-4 w-full mx-auto"
            >
              <div className="secondary-bg-color group space-y-4">
                {type === "add" && (
                  <InputComponent
                    ref={commentRef}
                    label={t("lang_name")}
                    required
                    name="name"
                    value={commentData?.name}
                    onChange={(e) => setCommentData({ ...commentData, name: e.target.value })}
                    placeholder="e.g. John"
                    autoFocus
                  />
                )}
                {type === "add" && (
                  <div className="w-full mb-4">
                    <label htmlFor="title" className="text-[var(--primary-text-color)] font-medium text-[13px] tracking-tight block mb-1">
                      {t("lang_Profile_Picture")}
                    </label>
                    <ImageUploader
                      alreadyImage={commentData?.profile_picture}
                      handleSetImage={handleSetImage}
                      folder="profile"
                      format="image"
                    />
                  </div>
                )}
                <TextareaComponent
                  ref={(el) => {
                    textareaRef.current = el;
                    if (commentRef) {
                      if (typeof commentRef === "function") commentRef(el);
                      else commentRef.current = el;
                    }
                  }}
                  label={t("lang_comment")}
                  required
                  name="comment"
                  value={commentData?.comment}
                  onChange={(e) =>
                    setCommentData({
                      ...commentData,
                      comment: e.target.value,
                    })
                  }
                  placeholder="Enter your comment here..."
                />
                {type === "add" && (
                  <div className="mb-4">
                    <SelectComponent
                      label={t("lang_Select_Portfolio")}
                      required
                      name="portfolioId"
                      placeholder="Search Portfolio"
                      value={commentData?.portfolioId}
                      loading={loading}
                      options={allPortfolios.map((p) => ({ value: p._id, label: p.title }))}
                      onChange={(e) =>
                        setCommentData({
                          ...commentData,
                          portfolioId: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
                <div className="w-full flex justify-end">
                  {isDisabled ? (
                    <ButtonComponent variant="primary" disabled>
                      {type === "add" ? "Add portfolio Comment" : "Update portfolio Comment"}
                    </ButtonComponent>
                  ) : (
                    <ButtonComponent
                      variant="primary"
                      onClick={(e) => handleAddPortfolioComment(e)}
                    >
                      {type === "add" ? "Add portfolio Comment" : "Update portfolio Comment"}
                    </ButtonComponent>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddPortfolioComments;
